<template>
  <div>
    <v-expand-transition v-if="existingRating">
      <div>
        <div class="tp-text-subtitle mb-3">
          {{ $t('components.greetReview.thankYouTitle') }}
        </div>
        <span class="tp-text-body">
          {{ $t('components.greetReview.thankYouMessage') }}
        </span>
      </div>
    </v-expand-transition>
    <div v-else>
      <div class="tp-text-subtitle mb-3">
        {{ $t('components.greetReview.title') }}
      </div>
      <v-chip-group
        v-model="ratingScore"
        active-class="black white--text"
        class="emoji-group d-flex flex-row"
        @change="submitGreetRating"
      >
        <div
          v-for="(rating, index) in ratings"
          :key="index"
          style="max-width: 20%"
          class="d-flex flex-column justify-center align-center emoji-chip"
        >
          <v-chip
            :key="rating"
            label
            color="#F5F5F5"
            x-large
            :value="ratingValues[index]"
            class="emoji-chip ma-1 pa-0 rounded-lg"
          >
            <div style="font-size: 36px" v-html="rating" />
          </v-chip>
          <span class="tp-text-label black--text mt-1">{{
            $t(ratingLabel(index))
          }}</span>
        </div>
      </v-chip-group>
      <v-expand-transition>
        <div v-show="ratingScore">
          <div class="tp-text-subtitle pt-6 pb-4">
            {{ $t('components.greetReview.tagsLabel') }}
          </div>
          <v-chip-group
            v-model="tagsSelection"
            multiple
            active-class="black white--text"
            column
            @change="submitGreetRatingTags"
          >
            <v-chip v-for="tag in tags" :key="tag" color="#F5F5F5" label>
              {{ $t(tag) }}
            </v-chip>
          </v-chip-group>
          <v-textarea
            v-model="ratingComment"
            class="comment-textarea mt-2"
            name="user-comment"
            rows="1"
            :placeholder="$t('components.greetReview.commentPlaceholder')"
            auto-grow
            outlined
            hide-details
            color="black"
          />
          <div class="px-4 mt-4">
            <Button :loading="loading" dark @click="submitGreetRatingComment">
              {{ $t('components.greetReview.ctaLabel') }}
            </Button>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import { debounce, compact } from 'lodash';
import Button from '@/components/Button.vue';

const RATINGS = ['&#128077;', '&#128078;'];

const RATING_LABELS = ['Good', 'Bad'];

const DEFAULT_TAGS = ['payment', 'control', 'speed', 'design', 'information'];

export default {
  components: {
    Button,
  },
  props: {
    uniqueOrderNumber: {
      type: String,
      required: false,
      default: null,
    },
    tipsQrCardHash: {
      type: String,
      required: false,
      default: null,
    },
    review: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      ratingScore: this.review?.rating || 0,
      ratingTags: this.review?.rating || 0,
      ratingComment: '',
      loading: false,
      ratingSubmitted: false,
      tagsSelection: [],
    };
  },
  computed: {
    existingRating() {
      if (this.ratingSubmitted) {
        return this.ratingScore;
      }
      return this.review?.submitted;
    },
    ratings() {
      return RATINGS;
    },
    tags() {
      return DEFAULT_TAGS;
    },
    ratingValues() {
      return {
        0: 'good',
        1: 'bad',
      };
    },
  },
  mounted() {
    this.tagsSelection = this.getInitialTagSelection();
  },
  methods: {
    ratingLabel(index) {
      return RATING_LABELS[index];
    },
    getInitialTagSelection() {
      if (!this.review?.tags) {
        return [];
      }
      return Object.keys(this.tags)
        .filter(key => this.review?.tags.includes(this.tags[key]))
        .map(Number);
    },
    submitGreetRating(rating) {
      if (!rating) {
        return;
      }

      this.$store.dispatch('submitGreetRating', {
        uniqueOrderNumber: this.uniqueOrderNumber,
        tipsQrCardHash: this.tipsQrCardHash,
        rating,
      });
    },
    submitGreetRatingTags: debounce(async function() {
      const selectedTags = this.tagsSelection.map(index => this.tags[index]);
      const tags = compact(selectedTags); // remove comment tag if present

      await this.$store.dispatch('submitGreetRatingTags', {
        tags,
      });
    }, 1000),
    async submitGreetRatingComment() {
      this.loading = true;
      await this.$store.dispatch('submitGreetRatingComment', {
        comment: this.ratingComment,
      });
      this.loading = false;
      this.ratingSubmitted = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.emoji-group::v-deep .v-slide-group__content {
  justify-content: center !important;
  padding: 0px !important;
  gap: 8px !important;
}

.emoji-chip {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
